import '../../styles/restScreens.css';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { supabaseClient } from '../../config/supabase';
import EngagementFormModal from './EngagementFormModal';
import EngagementDetailCard from './EngagementDetailCard';
import { STATUS_META, statusMeta, LIFECYCLE_STEPS, SIDE_EXITS } from './engagementConstants';
import useModuleAccess from '../../hooks/useModuleAccess';
import useTripPriceAccess from '../../hooks/useTripPriceAccess';
import { ENGAGEMENT_NO_PRICE_SELECT } from './engagementFields';
import { buildScheduleWindow } from './scheduleWindow';
import { excludeEventRecords } from '../../lib/eventEngagement';
import { loadFlightDetails, saveTripFlight } from '../../lib/tripFlight';
import { useCompanyWords } from '../../context/CompanyWordsContext';

// Module 0 — the human window onto engagements.
// Lists the logged-in owner's engagements (RLS scopes the rows automatically),
// and drives the create → confirm → lifecycle flow against Supabase.
function EngagementsView({ userId, orgId, theme = 'light', isUser = false }) {
    // UX pass: the company's own word (Admin Center > Company Words), e.g. Reservations. Default: Engagements.
    const words = useCompanyWords();
    const dark = theme === 'dark';
    const { allowed: dispatchEditAllowed } = useModuleAccess(isUser ? 'dispatch' : null, 'edit');
    const { allowed: financialEditAllowed } = useModuleAccess(isUser ? 'financial' : null, 'edit');
    const canEditDispatch = !isUser || dispatchEditAllowed;
    const canEditTripPrices = !isUser || financialEditAllowed || dispatchEditAllowed;
    const canShowPaymentStatus = useTripPriceAccess(isUser);
    const canShowTripPrices = canShowPaymentStatus || dispatchEditAllowed;
    const engagementFields = ENGAGEMENT_NO_PRICE_SELECT;

    const [engagements, setEngagements] = useState([]);
    const [entities, setEntities] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [services, setServices] = useState([]);
    const [zones, setZones] = useState([]);
    const [serviceLocations, setServiceLocations] = useState([]);
    const [serviceZoneRates, setServiceZoneRates] = useState([]);
    const [catalogError, setCatalogError] = useState(null);
    const [currentAssignment, setCurrentAssignment] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);

    const [showForm, setShowForm] = useState(false);
    const [formMode, setFormMode] = useState('create');
    const [editing, setEditing] = useState(null);
    const [selected, setSelected] = useState(null);

    const [statusFilter, setStatusFilter] = useState('all');
    const [entityFilter, setEntityFilter] = useState('all');
    const [search, setSearch] = useState('');

    const addTripPrices = useCallback(async (rows) => {
        if (!canShowTripPrices || rows.length === 0) return rows;
        const { data, error } = await supabaseClient.rpc(canShowPaymentStatus ? 'bl395a_engagement_prices' : 'gt_dispatch_fares', {
            p_engagement_ids: rows.map((row) => row.id),
        });
        if (error) throw error;
        const pricesByEngagement = new Map((data || []).map((price) => [price.engagement_id, price]));
        const rowsWithPrices = rows.map((row) => {
            const prices = pricesByEngagement.get(row.id);
            return prices ? { ...row, ...prices } : row;
        });

        if (!canShowPaymentStatus) return rowsWithPrices;
        const { data: paymentStatuses, error: paymentStatusError } = await supabaseClient.rpc(
            'bl395a_engagement_payment_status',
            { p_engagement_ids: rows.map((row) => row.id) }
        );
        if (paymentStatusError) throw paymentStatusError;
        const statusesByEngagement = new Map(
            (paymentStatuses || []).map((status) => [status.engagement_id, status.payment_status])
        );
        return rowsWithPrices.map((row) => {
            if (!statusesByEngagement.has(row.id)) {
                throw new Error(`Payment status is unavailable for engagement ${row.id}.`);
            }
            return { ...row, payment_status: statusesByEngagement.get(row.id) };
        });
    }, [canShowTripPrices, canShowPaymentStatus]);

    const persistScheduleStart = async (engagement, scheduledStart) => {
        if (!scheduledStart) return engagement;
        if (!orgId) throw new Error('Cannot save the pickup time without an active organization.');
        const { scheduled_start, scheduled_end } = buildScheduleWindow(
            scheduledStart,
            engagement.schedule?.scheduled_end
        );
        const scheduleFields = {
            org_id: orgId,
            confirmation_number: engagement.confirmation_number,
            scheduled_start,
            scheduled_end,
        };

        let scheduleId = engagement.schedule_id;
        if (scheduleId) {
            const { error } = await supabaseClient.from('schedule')
                .update({ scheduled_start, scheduled_end })
                .eq('schedule_id', scheduleId).eq('org_id', orgId);
            if (error) throw new Error(`Pickup time could not be saved: ${error.message}`);
        } else {
            const { data, error } = await supabaseClient.from('schedule')
                .insert([scheduleFields])
                .select('schedule_id')
                .single();
            if (error) throw new Error(`Pickup time could not be saved: ${error.message}`);
            scheduleId = data.schedule_id;
            const { error: linkError } = await supabaseClient.from('engagements')
                .update({ schedule_id: scheduleId })
                .eq('id', engagement.id);
            if (linkError) throw new Error(`Pickup time was created but not linked: ${linkError.message}`);
        }
        return {
            ...engagement,
            schedule_id: scheduleId,
            schedule: {
                scheduled_start,
                scheduled_end,
            },
        };
    };

    // ---- Data loading -------------------------------------------------------
    const loadEntities = useCallback(async () => {
        const { data, error } = await supabaseClient
            .from('entities')
            .select('id, entity_code, name')
            .eq('is_booking_node', true)
            .order('entity_code');
        if (!error) setEntities(data || []);
    }, []);

    const loadAccounts = useCallback(async () => {
        const { data, error } = await supabaseClient
            .from('accounts')
            .select('id, account_name')
            .order('account_name');
        if (!error) setAccounts(data || []);
    }, []);

    const searchClients = useCallback(async (query) => {
        const pattern = `%${query}%`;
        const [accountResult, riderResult] = await Promise.all([
            supabaseClient
                .from('accounts')
                .select('id, account_name')
                .ilike('account_name', pattern)
                .order('account_name')
                .limit(8),
            supabaseClient
                .from('contacts')
                .select('id, name, primary_account_id')
                .ilike('name', pattern)
                .order('name')
                .limit(8),
        ]);
        if (accountResult.error) throw accountResult.error;
        if (riderResult.error) throw riderResult.error;

        return [
            ...(accountResult.data || []).map((account) => ({
                id: account.id,
                label: account.account_name,
                kind: 'account',
            })),
            ...(riderResult.data || []).map((rider) => ({
                id: rider.id,
                label: rider.name,
                kind: 'rider',
                account_id: rider.primary_account_id,
            })),
        ];
    }, []);

    const loadServiceCatalog = useCallback(async () => {
        if (!orgId) {
            setCatalogError('Service catalog is unavailable because no organization is selected.');
            return;
        }
        const [serviceResult, zoneResult, locationResult, rateResult] = await Promise.all([
            supabaseClient.from('services').select('id, name, description, base_price, category_id').eq('org_id', orgId).eq('is_active', true).order('name'),
            supabaseClient.from('zones').select('id, name').eq('org_id', orgId).order('display_order').order('name'),
            supabaseClient.from('service_fixed_locations').select('id, service_id, label, address').eq('org_id', orgId).eq('is_active', true),
            supabaseClient.from('service_zone_rates').select('service_id, zone_id, rate').eq('org_id', orgId),
        ]);
        const failed = [serviceResult, zoneResult, locationResult, rateResult].find((result) => result.error);
        if (failed) {
            setCatalogError(failed.error.message || 'Unable to load the service catalog.');
            return;
        }
        setServices(serviceResult.data || []);
        setZones(zoneResult.data || []);
        setServiceLocations(locationResult.data || []);
        setServiceZoneRates(rateResult.data || []);
        setCatalogError(null);
    }, [orgId]);

    const loadEngagements = useCallback(async () => {
        setLoadError(null);
        try {
            const { data, error } = await supabaseClient
                .from('engagements')
                .select(`${engagementFields}, event_engagement_details(engagement_id), schedule:schedule!engagements_schedule_id_fkey(scheduled_start,scheduled_end)`)
                .order('created_at', { ascending: false });
            if (error) throw error;
            const rows = await addTripPrices(excludeEventRecords(data));
            setEngagements(rows);
            return rows;
        } catch (error) {
            setLoadError(error.message || 'Unable to load engagements.');
            setEngagements([]);
        } finally {
            setLoading(false);
        }
    }, [addTripPrices, engagementFields]);

    useEffect(() => {
        loadEntities();
        loadAccounts();
        loadEngagements();
        loadServiceCatalog();
    }, [loadEntities, loadAccounts, loadEngagements, loadServiceCatalog, userId]);

    useEffect(() => {
        let active = true;

        async function loadCurrentAssignment() {
            if (!selected?.id) {
                setCurrentAssignment(null);
                return;
            }

            setCurrentAssignment(null);
            const { data, error } = await supabaseClient
                .from('current_assignment')
                .select('engagement_id, driver_id, vehicle_id, status')
                .eq('engagement_id', selected.id)
                .maybeSingle();
            if (active) setCurrentAssignment(error ? null : data);
        }

        loadCurrentAssignment();
        return () => { active = false; };
    }, [selected?.id, selected?.updated_at]);

    // ---- Mutations ----------------------------------------------------------
    const createEngagement = async (payload) => {
        // FA-2: the flight is saved to the trip's structured flight fields (dispatch_engagement_details), not the engagements row
        const { scheduled_start: scheduledStart, flight, ...engagementPayload } = payload;
        const { data, error } = await supabaseClient
            .from('engagements')
            .insert([{ ...engagementPayload, status: 'requested', created_by: userId, channel: 'web' }])
            .select(`${engagementFields}, schedule:schedule!engagements_schedule_id_fkey(scheduled_start,scheduled_end)`)
            .single();
        if (error) throw error;
        const scheduledEngagement = await persistScheduleStart(data, scheduledStart);
        if (flight) await saveTripFlight(supabaseClient, data.id, flight);
        const [engagementWithPrices] = await addTripPrices([scheduledEngagement]);
        await loadEngagements();
        setShowForm(false);
        setSelected(engagementWithPrices); // drop straight into the detail view of the new engagement
    };

    const updateEngagement = async (payload) => {
        const { scheduled_start: scheduledStart, flight, ...engagementPayload } = payload;
        const { data, error } = await supabaseClient
            .from('engagements')
            .update({ ...engagementPayload, updated_by: userId })
            .eq('id', editing.id)
            .select(`${engagementFields}, schedule:schedule!engagements_schedule_id_fkey(scheduled_start,scheduled_end)`)
            .single();
        if (error) throw error;
        const scheduledEngagement = await persistScheduleStart(data, scheduledStart);
        // an edit clears a flight that was removed from the form, and saves a new or changed one
        if (flight || editing?.flight?.flight_number) await saveTripFlight(supabaseClient, data.id, flight || null);
        const [engagementWithPrices] = await addTripPrices([scheduledEngagement]);
        await loadEngagements();
        setShowForm(false);
        setEditing(null);
        if (selected && selected.id === data.id) setSelected(engagementWithPrices);
    };

    const changeStatus = async (newStatus, { cancelBoth = false } = {}) => {
        if (newStatus === 'canceled' && cancelBoth) {
            const { error } = await supabaseClient.rpc('m9_staff_cancel_pair', { p_engagement: selected.id });
            if (error) throw error;
        }
        const query = supabaseClient.from('engagements');
        const { data, error } = await (newStatus === 'canceled' && cancelBoth
            ? query.select(engagementFields) : query.update({ status: newStatus, updated_by: userId }).select(engagementFields))
            .eq('id', selected.id)
            .single();
        if (error) throw error;
        const [engagementWithPrices] = await addTripPrices([data]);
        setSelected(engagementWithPrices); // reflects the freshly-stamped confirmation number on confirm
        await loadEngagements();
    };

    // ---- Modal openers ------------------------------------------------------
    const openCreate = () => { setFormMode('create'); setEditing(null); setShowForm(true); };
    const openEdit = async (engagement) => {
        // FA-2: the edit form shows the trip's saved flight
        const flights = await loadFlightDetails(supabaseClient, [engagement?.id]);
        setFormMode('edit'); setEditing({ ...engagement, flight: flights[engagement?.id] || null }); setSelected(null); setShowForm(true);
    };

    // ---- Filtering ----------------------------------------------------------
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return engagements.filter((e) => {
            if (statusFilter !== 'all' && e.status !== statusFilter) return false;
            if (entityFilter !== 'all' && e.entity_id !== entityFilter) return false;
            if (!q) return true;
            return [e.confirmation_number, e.service_type, e.pickup_address, e.dropoff_address, e.origin, e.destination]
                .some((v) => (v || '').toLowerCase().includes(q));
        });
    }, [engagements, statusFilter, entityFilter, search]);

    const entityCode = (id) => entities.find((e) => e.id === id)?.entity_code || '—';
    const accountName = (id) => accounts.find((a) => a.id === id)?.account_name || '—';

    // ---- Styles -------------------------------------------------------------
    const cardCls = 'bg-[var(--surface-raised)] border-[var(--surface-border)]';
    const headCls = dark ? 'text-gray-400 border-gray-700' : 'text-gray-500 border-gray-200';
    const rowCls = dark ? 'border-gray-700 hover:bg-gray-700/40' : 'border-gray-100 hover:bg-[var(--accent-ink)]/10';
    const controlCls = `px-3 py-2 border rounded-lg ${dark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-800'}`;

    const StatusPill = ({ status }) => {
        const m = statusMeta(status);
        return <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${m.pill}`}>{m.icon} {m.label}</span>;
    };

    return (
        <div data-m6-layout="engagements" data-m6-component="EngagementsView" className="h-full flex flex-col">
            {/* Header / controls */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div>
                    <h1 className={`text-2xl font-bold ${'text-[var(--ink)]'}`}>🧭 {words.bookings}</h1>
                    <p className={`text-sm ${dark ? 'text-gray-400' : 'text-gray-500'}`}>Every job the business takes on — one permanent confirmation number each.</p>
                </div>
                {canEditDispatch && <button onClick={openCreate} className="px-4 py-2 bg-[var(--btn-fill)] text-[var(--btn-ink)] rounded-lg hover:bg-[var(--btn-fill-hover)] font-medium">
                    ➕ New {words.booking}
                </button>}
            </div>

            <div className="flex flex-wrap items-center gap-3 mb-4">
                <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search confirmation #, service, address…"
                    className={`${controlCls} flex-1 min-w-[220px]`}
                />
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={controlCls}>
                    <option value="all">All statuses</option>
                    {[...LIFECYCLE_STEPS, ...SIDE_EXITS].map((s) => (
                        <option key={s} value={s}>{STATUS_META[s].label}</option>
                    ))}
                </select>
                <select value={entityFilter} onChange={(e) => setEntityFilter(e.target.value)} className={controlCls}>
                    <option value="all">All hubs</option>
                    {entities.map((ent) => (
                        <option key={ent.id} value={ent.id}>{ent.entity_code}{ent.name ? ` — ${ent.name}` : ''}</option>
                    ))}
                </select>
                <span className={`text-sm ${dark ? 'text-gray-400' : 'text-gray-500'}`}>{filtered.length} shown</span>
            </div>

            {/* List */}
            <div className={`flex-1 overflow-auto rounded-xl border ${cardCls}`}>
                {loading ? (
                    <div className="p-10 text-center text-gray-500">Loading {words.bookingsLower}…</div>
                ) : loadError ? (
                    <div className="p-6 text-center text-red-600">Could not load {words.bookingsLower}: {loadError}</div>
                ) : filtered.length === 0 ? (
                    <div className="p-10 text-center">
                        <div className="text-4xl mb-2">🧭</div>
                        <div className={`font-medium ${dark ? 'text-gray-200' : 'text-gray-700'}`}>No {words.bookingsLower} yet</div>
                        <div className={`text-sm mb-4 ${dark ? 'text-gray-400' : 'text-gray-500'}`}>Create the first one to get started.</div>
                        {canEditDispatch && <button onClick={openCreate} className="px-4 py-2 bg-[var(--btn-fill)] text-[var(--btn-ink)] rounded-lg hover:bg-[var(--btn-fill-hover)] font-medium">➕ New {words.booking}</button>}
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className={`text-left border-b uppercase text-xs tracking-wide ${headCls}`}>
                                <th className="px-4 py-3">Confirmation #</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3">Hub</th>
                                <th className="px-4 py-3">Client</th>
                                <th className="px-4 py-3">Service</th>
                                <th className="px-4 py-3">Pickup → Dropoff</th>
                                <th className="px-4 py-3">Created</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((e) => (
                              <React.Fragment key={e.id}>
                                <tr onClick={() => setSelected(selected?.id === e.id ? null : e)}
                                    aria-expanded={selected?.id === e.id}
                                    className={`border-b cursor-pointer ${rowCls} ${selected?.id === e.id ? 'bg-[var(--surface-sunken)]' : ''}`}>
                                    <td className={`px-4 py-3 font-mono ${dark ? 'text-gray-100' : 'text-gray-800'}`}>
                                        {e.confirmation_number || <span className="text-gray-400 italic font-sans">unassigned</span>}
                                    </td>
                                    <td className="px-4 py-3"><StatusPill status={e.status} /></td>
                                    <td className={`px-4 py-3 ${dark ? 'text-gray-300' : 'text-gray-700'}`}>{entityCode(e.entity_id)}</td>
                                    <td className={`px-4 py-3 ${dark ? 'text-gray-300' : 'text-gray-700'}`}>{accountName(e.account_id)}</td>
                                    <td className={`px-4 py-3 ${dark ? 'text-gray-300' : 'text-gray-700'}`}>{e.service_type || '—'}</td>
                                    <td className={`px-4 py-3 ${dark ? 'text-gray-400' : 'text-gray-600'}`}>
                                        {(e.pickup_address || e.origin || '—')} <span className="text-gray-400">→</span> {(e.dropoff_address || e.destination || '—')}
                                    </td>
                                    <td className={`px-4 py-3 ${dark ? 'text-gray-400' : 'text-gray-500'}`}>{e.created_at ? new Date(e.created_at).toLocaleDateString() : '—'}</td>
                                </tr>
                                {/* The cascade: opens IN PLACE, under the row it came from, so you
                                    never lose your place in the list. Same grammar as the CRM. */}
                                {selected?.id === e.id && (
                                    <tr>
                                        <td colSpan={7} className="px-4 pb-4 pt-1 bg-[var(--surface-canvas)]">
                                            <EngagementDetailCard
                                                engagement={selected}
                                                currentAssignment={currentAssignment?.engagement_id === selected.id ? currentAssignment : null}
                                                entities={entities}
                                                accounts={accounts}
                                                currentUserId={userId}
                                                onClose={() => setSelected(null)}
                                                onEdit={openEdit}
                                                onChangeStatus={changeStatus}
                                                onTripChanged={async () => {
                                                    const rows = await loadEngagements();
                                                    setSelected((current) => {
                                                        const fresh = rows?.find((row) => row.id === current?.id);
                                                        return fresh ? { ...current, ...fresh } : current;
                                                    });
                                                }}
                                                theme={theme}
                                                canEdit={canEditDispatch}
                                                canShowTripPrices={canShowTripPrices}
                                                canShowPaymentStatus={canShowPaymentStatus}
                                            />
                                        </td>
                                    </tr>
                                )}
                              </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Modals */}
            {showForm && (
                <EngagementFormModal
                    mode={formMode}
                    engagement={editing}
                    entities={entities}
                    accounts={accounts}
                    services={services}
                    zones={zones}
                    serviceLocations={serviceLocations}
                    serviceZoneRates={serviceZoneRates}
                    catalogError={catalogError}
                    onSearchClients={searchClients}
                    onClose={() => { setShowForm(false); setEditing(null); }}
                    onSave={formMode === 'edit' ? updateEngagement : createEngagement}
                    theme={theme}
                    showTripPrices={canShowTripPrices}
                    showPaymentStatus={canShowPaymentStatus}
                    canEditTripPrices={canEditTripPrices}
                    canEditFinalAmounts={canEditTripPrices}
                />
            )}


        </div>
    );
}

export default EngagementsView;
